import { usePrevious } from '@sequencehq/utils'
import {
  CorePortErrors,
  CubeReducerState
} from 'modules/Cube/domain/cube.domain.types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  CubeDomainInterface,
  useCubeDomain
} from 'modules/Cube/domain/cube.domain'
import { STANDARD_AVAILABLE_FREQUENCIES } from 'modules/Cube/domain/cube.constants'
import { useBillingSchedulePortImplementation } from 'modules/Cube/communication/external/billingSchedule.api.v1/usePorts.billingSchedule.api.v1'
import { match } from 'ts-pattern'
import { useQuotePorts } from 'modules/Cube/communication/external/quotes.api.v1/usePorts.quote.api.v1'
import { useQuoteTemplatePorts } from 'Cube/communication/external/quoteTemplates.api.v1/usePorts.quoteTemplates.api.v1'

type UseCubeRoot = (props: {
  variant: 'schedule' | 'quote' | 'quoteTemplate'
}) => {
  editorContext: CubeDomainInterface
  ready: boolean
  error: CorePortErrors | null
}

enum LoadingStatus {
  UNINITIALIZED = 'UNINITIALIZED',
  RELOADING = 'RELOADING',
  LOADING = 'LOADING',
  READY = 'READY',
  ERROR = 'ERROR'
}

/**
 * The top level hook used to initialize and 'stitch together' mechanisms like
 * loading and the editor itself.
 * @returns
 */
export const useCubeRoot: UseCubeRoot = props => {
  const { id } = useParams<{ id: string }>()
  const flags = useFeatureFlags()
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
    LoadingStatus.UNINITIALIZED
  )
  const [corePortError, setCorePortError] = useState<CorePortErrors | null>(
    null
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const defaultCustomerId =
    searchParams.get('customerId') ?? searchParams.get('sequenceCustomerId')
  const defaultSalesforceOpportunityId = searchParams.get(
    'salesforceOpportunityId'
  )
  const defaultQuoteTemplateId = searchParams.get('quoteTemplateId')

  const billingScheduleApiV1PortImplementation =
    useBillingSchedulePortImplementation({
      scheduleId: id === 'new' ? undefined : id
    })

  const quoteV1PortImplementation = useQuotePorts({
    quoteId: id === 'new' ? undefined : id
  })

  const quoteTemplateV1PortImplementation = useQuoteTemplatePorts({
    quoteTemplateId: id === 'new' ? undefined : id
  })

  const {
    showNewTaxManagement,
    usePhasePriceMetadataEditing,
    useCreditGrantLedger,
    useNetsuite,
    useStackedDiscounts,
    usePhaseContractReferences
  } = flags

  /**
   * This is the base configuration - some aspects of the configuration may be
   * changed as part of the loading process. Notably features which are controlled
   * by whether a give customer has access a feature. To understand the final configration
   * state for a given port implementation, check the onLoad of that port.
   */
  const baseConfiguration: CubeReducerState['configuration'] = useMemo(() => {
    return {
      permissions: {},
      features: {
        showNewTaxManagement: showNewTaxManagement ?? false,
        stripeIntegrationAvailable: false,
        goCardlessIntegrationAvailable: false,
        showReferenceField: true,
        showLabelField: true,
        /**
         * Milestone billing can only be enabled for schedules right now - we have
         * no way to persist the option in a quote context.
         */
        phaseRecurrenceEditing: true,
        useMilestoneBilling: true,
        phasePriceMetadataEditing: usePhasePriceMetadataEditing ?? false,
        /**
         * V2 credit grants embedded in the schedule save. Gated on the
         * useCreditGrantLedger flag; schedule-only, forced off for quotes and
         * templates below (they cannot persist grants).
         */
        creditGrantsEditing: useCreditGrantLedger ?? false,
        /**
         * Contract tags on phases, currently used for the NetSuite integration only.
         * Only available when editing schedules,
         */
        contractTagEditing: useNetsuite ?? false,
        /**
         * Quote, opportunity, NetSuite and other references on phases. They
         * replace the contract tag above, and are only available when editing
         * schedules.
         */
        contractReferenceEditing: usePhaseContractReferences ?? false,
        /**
         * Single-day phases are only permitted for schedules. Quotes and quote
         * templates keep the default 2-day minimum phase duration.
         */
        allowSingleDayDuration: true,
        stackedDiscounts: useStackedDiscounts
      },
      /**
       * What the editor falls back to when the account has no stored setting.
       * These match the backend's own defaults, so an account that never opens
       * the settings page behaves exactly as it did before the setting existed.
       *
       * The stored values come from the load port, which folds them into the
       * configuration - a hook here would still be undefined on the first
       * render after a cold load, and the first "Add phase" click could use the
       * wrong default.
       */
      editorDefaults: {
        phaseEditingMode: 'SINGLE',
        resetBillingPeriod: 'RESET'
      },
      currency: {
        default: 'GBP',
        enabled: ['GBP']
      },
      availableFrequencies: STANDARD_AVAILABLE_FREQUENCIES
    }
  }, [
    showNewTaxManagement,
    usePhasePriceMetadataEditing,
    useCreditGrantLedger,
    useNetsuite,
    useStackedDiscounts,
    usePhaseContractReferences
  ])

  const portImplementation = useMemo(() => {
    return match(props.variant)
      .with('schedule', () =>
        billingScheduleApiV1PortImplementation({
          configuration: baseConfiguration,
          defaultValues: {
            customerId: defaultCustomerId ?? undefined
          }
        })
      )
      .with('quote', () =>
        quoteV1PortImplementation({
          configuration: {
            ...baseConfiguration,
            features: {
              ...baseConfiguration.features,
              /**
               * These features are permanently disabled for quotes.
               */
              useMilestoneBilling: false,
              phasePriceMetadataEditing: false,
              creditGrantsEditing: false,
              contractTagEditing: false,
              contractReferenceEditing: false,
              allowSingleDayDuration: false,
              stackedDiscounts: useStackedDiscounts
            },
            /**
             * Pinned rather than read from settings: a quote cannot persist
             * `recurrencePreference`, and its pricing drawers edit a single
             * phase at a time.
             */
            editorDefaults: {
              phaseEditingMode: 'SINGLE',
              resetBillingPeriod: 'CONTINUE_FROM_PREVIOUS_PHASE'
            }
          },
          defaultValues: {
            customerId: defaultCustomerId ?? undefined,
            salesforceOpportunityId:
              defaultSalesforceOpportunityId ?? undefined,
            quoteTemplateId: defaultQuoteTemplateId ?? undefined
          }
        })
      )
      .with('quoteTemplate', () =>
        quoteTemplateV1PortImplementation({
          configuration: {
            ...baseConfiguration,
            features: {
              ...baseConfiguration.features,
              /**
               * These features are permanently disabled for quotes.
               */
              useMilestoneBilling: false,
              phasePriceMetadataEditing: false,
              creditGrantsEditing: false,
              contractTagEditing: false,
              contractReferenceEditing: false,
              allowSingleDayDuration: false,
              stackedDiscounts: useStackedDiscounts
            },
            /**
             * Pinned rather than read from settings: a quote template cannot
             * persist `recurrencePreference`, and its pricing drawers edit a
             * single phase at a time.
             */
            editorDefaults: {
              phaseEditingMode: 'SINGLE',
              resetBillingPeriod: 'CONTINUE_FROM_PREVIOUS_PHASE'
            }
          }
        })
      )
      .exhaustive()
  }, [
    baseConfiguration,
    billingScheduleApiV1PortImplementation,
    props.variant,
    quoteV1PortImplementation,
    defaultCustomerId,
    defaultSalesforceOpportunityId,
    defaultQuoteTemplateId,
    useStackedDiscounts
  ])

  const cubeDomain = useCubeDomain({
    ports: portImplementation
  })

  const reloadDomain = cubeDomain.mutators.external.in.core

  const previousRouteId = usePrevious(id)

  useEffect(() => {
    /** Force a reload when the query params 'reset' is set to true, or if the route id has changed */
    if (searchParams.get('reset') === 'true') {
      setLoadingStatus(LoadingStatus.LOADING)
      /**
       * Ensures that, if a reset has been set, that it is removed.
       */
      setSearchParams(
        defaultCustomerId ? { customerId: defaultCustomerId } : {},
        {
          replace: true
        }
      )

      void reloadDomain().then(({ data, error }) => {
        if (error || !data) {
          setCorePortError(error || CorePortErrors.Other)
          setLoadingStatus(LoadingStatus.ERROR)
          return
        }

        setLoadingStatus(LoadingStatus.READY)
      })
      return
    }

    if (previousRouteId && previousRouteId !== id) {
      setLoadingStatus(LoadingStatus.LOADING)
      void reloadDomain().then(({ data, error }) => {
        if (error || !data) {
          setCorePortError(error || CorePortErrors.Other)
          setLoadingStatus(LoadingStatus.ERROR)
          return
        }

        setLoadingStatus(LoadingStatus.READY)
      })
      return
    }

    if (loadingStatus === LoadingStatus.UNINITIALIZED) {
      setLoadingStatus(LoadingStatus.LOADING)
      void reloadDomain().then(({ data, error }) => {
        if (error || !data) {
          setCorePortError(error || CorePortErrors.Other)
          setLoadingStatus(LoadingStatus.ERROR)
          return
        }

        setLoadingStatus(LoadingStatus.READY)
      })
    }
  }, [
    defaultCustomerId,
    setSearchParams,
    reloadDomain,
    searchParams,
    id,
    previousRouteId
  ])

  return {
    editorContext: cubeDomain,
    error:
      loadingStatus === LoadingStatus.ERROR
        ? corePortError || CorePortErrors.Other
        : null,
    ready:
      (loadingStatus === LoadingStatus.READY ||
        loadingStatus === LoadingStatus.RELOADING) &&
      cubeDomain.queries.rawData.editor.loaded
  }
}
